import { z } from "zod";

/**
 * Cuentas del equipo interno que accede al panel de administración.
 *
 * Viven en su propia colección, separadas de `users`, por dos razones:
 *
 * 1. **Seguridad.** Antes el acceso al panel era un booleano (`vm_admin`)
 *    dentro del documento de un usuario de la plataforma médica: un
 *    `users/{uid}` mal editado convertía a un paciente en administrador.
 * 2. **Modelado.** Un documento de `users` arrastra `blood_type`,
 *    `attendances`, `schedule`, `prefix_dicom`… campos clínicos que no tienen
 *    sentido en una cuenta de equipo. Son dos entidades distintas.
 *
 * OJO con Firebase Auth: es un único pool de identidades por proyecto. Separar
 * la colección NO crea dos sistemas de login. Lo que consigue es que una misma
 * persona tenga DOS cuentas de Auth distintas (emails distintos → uids
 * distintos): la suya de paciente en la plataforma médica, y la del equipo bajo
 * el dominio corporativo. La separación real vive en qué colección tiene el
 * perfil.
 */

/** Dominio obligatorio de las cuentas del equipo. */
export const STAFF_EMAIL_DOMAIN = "@viewmedonline.com";

/**
 * Parte local del correo (antes de @): minúsculas, sin espacios.
 * Si pegan un correo completo, se queda solo con lo anterior a @.
 */
export function normalizeStaffEmailLocalPart(value: string): string {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return "";
  const at = trimmed.indexOf("@");
  return at === -1 ? trimmed : trimmed.slice(0, at);
}

/** Arma el correo corporativo a partir de la parte local. */
export function buildStaffEmail(localPart: string): string {
  return `${normalizeStaffEmailLocalPart(localPart)}${STAFF_EMAIL_DOMAIN}`;
}

/**
 * ¿La parte local es usable para un correo del equipo?
 * Letras, números y . _ + - ; no vacía.
 */
export function isValidStaffEmailLocalPart(localPart: string): boolean {
  const local = normalizeStaffEmailLocalPart(localPart);
  if (!local || local.length > 64) return false;
  if (!/^[a-z0-9._+-]+$/.test(local)) return false;
  if (local.startsWith(".") || local.endsWith(".") || local.includes("..")) {
    return false;
  }
  return isStaffEmail(buildStaffEmail(local));
}

/**
 * ¿El correo pertenece al dominio corporativo?
 *
 * Es una regla de HIGIENE, no un control de seguridad: cualquiera puede
 * escribir un correo. Lo que de verdad concede acceso es que exista el
 * documento `staff/{uid}` y esté activo. Esta comprobación solo evita que se
 * den de alta cuentas de equipo con Gmail o con el correo personal de alguien.
 */
export function isStaffEmail(email: string | null | undefined): boolean {
  return typeof email === "string" && email.toLowerCase().endsWith(STAFF_EMAIL_DOMAIN);
}

/** Documento `staff/{uid}` ya normalizado. */
export const staffMemberSchema = z.object({
  /** == id del documento == uid de Firebase Auth. */
  uid: z.string().min(1),
  email: z.string().min(1),
  name: z.string().default(""),
  /** Cargo o área, solo informativo. */
  position: z.string().default(""),
  /** Un miembro inactivo conserva su documento pero no puede entrar. */
  active: z.boolean().default(true),
  /** Uid en `users` del que se migró, si vino de una cuenta antigua. */
  migratedFromUserUid: z.string().nullable().default(null),
  createdAt: z.date().nullable().default(null),
  updatedAt: z.date().nullable().default(null),
  updatedBy: z.string().nullable().default(null),
});

export type StaffMember = z.infer<typeof staffMemberSchema>;

export const createStaffMemberSchema = z.object({
  uid: z.string().min(1, "El uid de la cuenta es obligatorio"),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .refine(isStaffEmail, {
      message: `El correo del equipo debe pertenecer a ${STAFF_EMAIL_DOMAIN}`,
    }),
  name: z.string().trim().max(200).default(""),
  position: z.string().trim().max(120).default(""),
  active: z.boolean().default(true),
  migratedFromUserUid: z.string().nullable().default(null),
});

export type CreateStaffMemberInput = z.input<typeof createStaffMemberSchema>;

export const updateStaffMemberSchema = z.object({
  name: z.string().trim().max(200).optional(),
  position: z.string().trim().max(120).optional(),
  active: z.boolean().optional(),
});

export type UpdateStaffMemberInput = z.input<typeof updateStaffMemberSchema>;
